import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { FilterValues } from "../../../../data-model/filterValues";
import {
  processFilterData,
  transformDateFields,
  transformObjectToFacetFiltersForPostgres,
} from "../../../../utils/queryConstraint";

const HITS_PER_PAGE = 100;

/**
 * Builds a Postgres search payload from the active filters, search term, and org ID.
 * Processes filter values into facet filter format and extracts date-range constraints.
 * When a sort field is provided, it is included as `sort_by` / `sort_dir` so the API
 * returns rows in the requested order.
 *
 * Optional `additionalFacetFilters` are merged on top of the user's filters so
 * route-derived defaults (e.g. `master_auth_status: orders_to_process` on the
 * `/orders-to-process` route) can be applied to every request without
 * mutating Redux filter state. Conflicting keys win in favor of
 * `additionalFacetFilters` — matching the override semantics already used by
 * `generateFilterPayloadForGenericAuthStatus`.
 *
 * @param validFilters - Active filter values for the medical PA orders table
 * @param searchTerm - Free-text search query
 * @param orgId - Organization/facility ID to scope the search
 * @param sortByField - Optional canonical API field name to sort by
 * @param sortAscending - Optional sort direction; defaults to ascending when sortByField is set
 * @param additionalFacetFilters - Extra facet filters merged on top of `validFilters`
 * @returns A fully formed search payload ready for the Postgres search API
 */
export const generateFilterPayloadForMedicalPaOrders = (
  validFilters: FilterValues[],
  searchTerm: string,
  orgId: string,
  sortByField?: string,
  sortAscending?: boolean,
  additionalFacetFilters?: Record<string, string[]>,
): SearchForMedicalPaOrdersFromPostgresParams => {
  const filterData = processFilterData(validFilters);
  const mergedFilterData = additionalFacetFilters
    ? { ...filterData, ...additionalFacetFilters }
    : filterData;
  const createdFilterQuery =
    transformObjectToFacetFiltersForPostgres(mergedFilterData);
  const dateFilterQuery = transformDateFields(mergedFilterData);

  return {
    org_id: orgId || "",
    query: searchTerm ?? "",
    facet_filters: createdFilterQuery,
    hits_per_page: HITS_PER_PAGE,
    page: 0,
    ...dateFilterQuery,
    ...(sortByField
      ? {
          sort_by: sortByField,
          sort_dir: sortAscending === false ? "desc" : "asc",
        }
      : {}),
  };
};
